// 本週期階段時間軸（W58.5）—— **取代**「近期關鍵時間」與「這一個月階段」。
//
// 使用者的反饋：
//
// > **「目前『週期摘要』、『近期關鍵時間』與『這一個月階段』三個區塊資訊高度重複。
// > 請將『近期關鍵時間』與『這一個月階段』二合一，整合成一個視覺化的
// > 『本週期階段時間軸』，直接標示各階段起止日期與『今天』所在位置，
// > 刪除冗餘列表。」**
//
// **重疊是結構性的，不是措辭問題。** 排卵日與易孕期**既是**「關鍵日期」
// **又是**「階段」；而「下次經期」是**下一輪**的第 1 天 —— 把它放在
// 「本輪的階段」旁邊，讀者要自己去對「那是下一輪還是這一轮」。
//
// **這一輪把兩件事收成一件：**
//
// - 階段的**起訖日期**（原來的階段清單）
// - 階段的**長短**（原來只有一個「N 天」的文字，現在是**正比於天數的橫條**）
//
// 而「今天所在的位置」由**掛在當前階段那一列的「今天」膠囊**講。
//
// **後端不用改。** `projection.phases[]` 已經帶了每一段的
// `phase` / `label` / `start_date` / `end_date` / `days`，而 `phase_start` /
// `phase_end` 已經指出了「今天在哪一段」。這是**前端的重組，不是新的演算法**
// （與 W58.0 同一條原則）。見 `DECISIONS.md` ADR-0210。

import { cn } from "@/lib/utils/cn";
import type { CyclePhase, CyclePhaseKind, CycleProjection } from "@/lib/api/calendar";

export interface CyclePhaseTimelineProps {
  projection: CycleProjection;
  /** 下一次經期的開始日（`cycle.predicted_start`）。 */
  predictedStart: string | null;
  /** 今天（`YYYY-MM-DD`）。`null` = 不知道今天在哪，於是沒有「今天」膠囊。 */
  today: string | null;
}

/** `M/D` —— 這個頁面上的日期只有月日才有意義（跨年不常見）。 */
function fmt(d: string | null | undefined): string {
  if (!d) return "—";
  return `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}`;
}

/**
 * The bar's colour — **與月曆格同一套語言**（W58.5）。
 *
 * 玫瑰講經期、紫講受孕、淺灰講安全期（泡期 + 黃體期）。
 * 月曆格上「排卵日」與「排卵期」底色一樣，而這裡它們是同一個階段家族 ——
 * 時間軸講的是**階段**，不是**狀態**（實際 / 推算），所以不需要分開。
 */
function phaseBarClass(phase: CyclePhaseKind): string {
  switch (phase) {
    case "menstrual":
      return "bg-partner";
    case "fertile":
      return "bg-shared/50";
    case "ovulation":
      return "bg-shared";
    default:
      // 卵泡期 / 黃體期 —— 模型裡存在，但它們合起來就是**安全期**。
      return "bg-hairline-strong";
  }
}

export function CyclePhaseTimeline({
  projection,
  predictedStart,
  today,
}: CyclePhaseTimelineProps) {
  const phases = projection.phases ?? [];
  if (phases.length === 0) return null;

  const maxDays = Math.max(...phases.map((p) => Math.max(1, p.days)), 1);
  const first = phases[0];
  const last = phases[phases.length - 1];
  if (!first || !last) return null;

  // 「今天」落在哪一段？—— 用**日期區間**找，不用 phase_start 相等：
  // 相等在「今天正好是第一天的時候」成立，而區間在每一種情況下都成立。
  const currentPhase = today
    ? phases.find((p) => p.start_date <= today && today <= p.end_date)
    : undefined;

  return (
    <section className="flex flex-col gap-s-2" data-testid="calendar-cycle-timeline">
      <h2 className="text-micro uppercase tracking-wide text-ink-3">本週期階段</h2>
      {/* 標題行 —— **「下次經期」在這裡**。它是**下一輪**的第 1 天，而這一輪
          的範圍就是 phases 的第一段到最後一段。把它放在這裡，讀者一眼就知道
          「這一輪到哪裡結束、下一輪從哪裡開始」，而不用在兩張表之間來回對。 */}
      <p className="text-body-sm text-ink-2" data-testid="calendar-cycle-timeline-range">
        本輪 {fmt(first.start_date)} – {fmt(last.end_date)}
        {predictedStart ? (
          <>
            {" · "}
            <span className="text-ink-1">下次經期 {fmt(predictedStart)}</span>
          </>
        ) : null}
      </p>

      <ul className="flex flex-col divide-y divide-hairline rounded-md border border-hairline">
        {phases.map((p: CyclePhase) => {
          const isCurrent = currentPhase?.phase === p.phase;
          const width = Math.max(6, Math.round((p.days / maxDays) * 100));
          return (
            <li key={p.phase} className="flex items-center gap-s-3 p-s-3">
              <span
                className={cn(
                  "h-[3px] w-s-3 shrink-0 rounded-pill",
                  phaseBarClass(p.phase as CyclePhaseKind),
                )}
                aria-hidden="true"
              />
              <span className="w-s-12 shrink-0 whitespace-nowrap text-body-sm text-ink-1">
                {p.label}
              </span>
              <span className="whitespace-nowrap text-body-sm tabular-nums text-ink-2">
                {fmt(p.start_date)} – {fmt(p.end_date)}
              </span>
              {/* **長度正比於天數。** 這是時間軸相對清單唯一多出來的資訊 ——
                  「黃體期比經期長很多」一眼看見，而清單要逐列比數字。

                  **手機上也要看得到** —— 這一段是整個時間軸存在的理由，而
                  390px 的螢幕放得下（標籤 48 + 日期 ~85 + 條 40 + 今天 28）。 */}
              <span className="ml-auto h-[3px] w-s-10 shrink-0 rounded-pill bg-hairline">
                <span
                  className={cn("block h-full rounded-pill", phaseBarClass(p.phase as CyclePhaseKind))}
                  style={{ width: `${width}%` }}
                />
              </span>
              {isCurrent ? (
                <span
                  className="shrink-0 rounded-pill bg-ink-1 px-s-2 py-s-0 text-micro font-semibold text-paper"
                  data-testid="calendar-cycle-timeline-today"
                >
                  今天
                </span>
              ) : (
                <span className="shrink-0 text-meta tabular-nums text-ink-3">{p.days} 天</span>
              )}
            </li>
          );
        })}
      </ul>

      {projection.safe_days > 0 ? (
        <p className="text-meta text-ink-2">
          {/* W58.1 — 安全期 = 卵泡期 + 黃體期。它在月曆格上**不畫任何記號**
              （安全期就是「什麼都沒有」的日子，像 Meetyou 的白底），
              所以名字與天數在這裡講清楚。 */}
          <span data-testid="calendar-cycle-safe-days">
            安全期 {projection.safe_days} 天（卵泡期 + 黃體期）
          </span>
        </p>
      ) : null}
    </section>
  );
}
